import * as React from "react";
import { Slider } from "./ui/slider";
import { cn, formatHelp } from "../lib/utils";

interface SliderWithInputProps
  extends React.ComponentPropsWithoutRef<typeof Slider> {
  suffix?: string;
  prefix?: string;
  formatter?: "currency" | "number" | undefined;
  capped?: boolean;
  max: number;
}

const SliderWithInput = React.forwardRef<
  React.ElementRef<typeof Slider>,
  SliderWithInputProps
>(
  (
    {
      className,
      min,
      max,
      value,
      onValueChange,
      defaultValue,
      formatter,
      prefix,
      suffix,
      id,
      capped,
      ...props
    },
    ref
  ) => {
    const [currentValue, setCurrentValue] = React.useState(value);

    // Update the local state when the value prop changes.
    React.useEffect(() => {
      setCurrentValue(value);
    }, [value]);

    const handleSliderChange = (newValue: number[]) => {
      setCurrentValue(newValue);
      if (onValueChange) {
        onValueChange(newValue);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let val = e.target.value;
      //   remove commas from input
      val = val.toString().replace(/,/g, "");
      let newValue = parseInt(val) || 0;

      if (capped && newValue > max) {
        newValue = max;
      }

      if (newValue === 0) {
        newValue = min || 0;
      }

      setCurrentValue([newValue]);
      if (onValueChange) {
        onValueChange([newValue]);
      }
    };

    return (
      <>
        <div
          className="dw-flex dw-w-full dw-items-baseline dw-gap-x-2 dw-text-tertiary"
          id={id}
        >
          {prefix && (
            <span className="dw-text-5xl md:dw-text-6xl dw-font-bold">
              {prefix}
            </span>
          )}
          <input
            type="text"
            className="dw-border-0 dw-my-2 dw-h-16 dw-text-5xl md:dw-text-6xl dw-font-bold dw-max-w-[300px] dw-outline-none dw-min-h-[60px]"
            min={min}
            id={`${id}-input`}
            size={formatter === "currency" ? 10 : 1}
            max={max}
            value={
              formatter
                ? formatHelp({
                    method: formatter,
                    value: currentValue as any,
                  })
                : (currentValue as any).toString()
            }
            onChange={handleInputChange}
          />
          {suffix && (
            <span className="dw-text-5xl md:dw-text-6xl dw-font-semibold">
              {suffix}
            </span>
          )}
        </div>
        <Slider
          ref={ref}
          id={`${id}-slider`}
          className={cn(
            "dw-relative dw-flex dw-w-full dw-touch-none dw-select-none dw-items-center",
            className
          )}
          min={min}
          max={max}
          defaultValue={defaultValue}
          value={currentValue}
          onValueChange={handleSliderChange}
          {...props}
        />
      </>
    );
  }
);

SliderWithInput.displayName = "SliderWithInput";

export { SliderWithInput };
